import * as React from "react";
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
  DiscardGuard,
} from "@/components/ui/dialog";
import { Callout, Skeleton } from "@/components/ui/feedback";
import { Field } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/misc";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/components/ui/toast";
import { EndpointPicker, type EndpointRef } from "@/features/endpoint-picker";
import { ApiError } from "@/lib/api";
import { sameValues } from "@/lib/form";
import { repNumberRule } from "@/lib/campaign";
import {
  useCampaignMembers, useCampaigns, useQueues, useSkills, useUpdateDid,
} from "@/lib/queries";
import {
  DEFAULT_TAG_LEVEL, LEVELS, assignableSkills, initialTags, numberTags, routeTakesTags,
  routingTagsPayload, taggedCallerWait, unheldTags,
} from "@/lib/skills";
import {
  ENDPOINT_ROUTES, ROUTE_DESCRIPTIONS, ROUTE_LABELS, ROUTE_TYPES,
  type Did, type RouteType, type RoutingSkill,
} from "@/lib/types";

/**
 * Where a call to this number goes.
 *
 * The route is sent as a whole — type plus the one target that type uses — because the server
 * treats it that way: switching from a queue to a rep drops the queue name rather than leaving
 * it behind. Campaign attribution on a non-campaign route is the one extra: it tags the
 * inbound leg for reporting without changing where it rings.
 *
 * "Rep's phone" and "Rep's room" share the endpoint picker, so changing your mind between them
 * keeps the rep you already chose. What differs is what the rep answers into: the phone route is
 * a plain bridge; the room route puts the call in a room of its own (ADR-032), where the rep can
 * hold the caller, bring colleagues in and hand the call over.
 *
 * Both offer only endpoints somebody holds. An unassigned endpoint has no rep to ring and no
 * console to open its room from, so routing a number at one produces a line that rings out
 * forever — the picker not offering it is the whole guard.
 *
 * A campaign route can name one of the campaign's reps (ADR-037): the caller still waits on the
 * campaign's line, and that rep's phone rings first. What happens when they cannot take the call is
 * the campaign's rule for all its rep numbers, shown here and set on the campaign's Routing tab.
 *
 * A campaign or queue route can also require skills of every caller (ADR-015): a Spanish number
 * into a campaign rings only its Spanish reps, while the campaign's main number rings anyone. The
 * tags go with the route, whole. Until the catalog has loaded they are left out of the save, which
 * the server reads as "keep them", so a slow or failed load never wipes a number's tags.
 */

const NONE = "__none__";
const UNROUTED = "__unrouted__";

/** Who answers a campaign number: anyone on its line, or one of its reps first (ADR-037). */
type WhoAnswers = "line" | "rep";

/** The form as the number stands: what opening the dialog fills in, and what an edit is measured
 *  against. */
function routeForm(did: Did) {
  return {
    routeType: did.action_type,
    campaignId: did.campaign_id,
    queueName: did.action_type === "queue" ? did.action_target : null,
    // Straight off the DID: no list has to load before the current route is legible, and it
    // stays legible for an endpoint that has since been unassigned and so is not on offer.
    endpoint:
      did.sip_endpoint_id === null
        ? null
        : {
            id: did.sip_endpoint_id,
            extension: did.sip_endpoint_extension ?? "",
            domain_name: did.sip_endpoint_domain_name,
            user_name: did.sip_endpoint_user_name,
          },
    who: (did.action_type === "campaign" && did.sip_endpoint_id !== null ? "rep" : "line") as WhoAnswers,
    repId: did.action_type === "campaign" ? did.sip_endpoint_id : null,
    soundFile: did.action_type === "hangup" ? (did.action_target ?? "") : "",
    description: did.description ?? "",
    // A number lands disabled and unrouted; giving it a route is what the switch is for, so
    // the toggle defaults on the first time it is routed.
    enabled: did.action_type === null ? true : did.enabled,
  };
}

export function RouteDidDialog({
  did, open, onOpenChange,
}: {
  did: Did | undefined;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const toast = useToast();
  const update = useUpdateDid();
  const [routeType, setRouteType] = React.useState<RouteType | null>(null);
  const [campaignId, setCampaignId] = React.useState<string | null>(null);
  const [queueName, setQueueName] = React.useState<string | null>(null);
  const [endpoint, setEndpoint] = React.useState<EndpointRef | null>(null);
  const [who, setWho] = React.useState<WhoAnswers>("line");
  const [repId, setRepId] = React.useState<number | null>(null);
  const [soundFile, setSoundFile] = React.useState("");
  const [description, setDescription] = React.useState("");
  const [enabled, setEnabled] = React.useState(true);
  /** The tags being edited: slug → minimum level. Null until something is ticked, so the stored
   *  tags show as soon as the catalog arrives. */
  const [tagDraft, setTagDraft] = React.useState<Record<string, number> | null>(null);
  const [formError, setFormError] = React.useState<string | null>(null);

  const { data: campaigns } = useCampaigns({}, { enabled: open });
  const { data: queues } = useQueues({ enabled: open });
  const members = useCampaignMembers(campaignId ?? "", {
    enabled: open && routeType === "campaign" && campaignId !== null,
  });
  const catalog = useSkills({ enabled: open });
  const needsEndpoint =
    routeType !== null && (ENDPOINT_ROUTES as readonly string[]).includes(routeType);
  const takesTags = routeTakesTags(routeType);

  React.useEffect(() => {
    if (!open || !did) return;
    const initial = routeForm(did);
    setFormError(null);
    setRouteType(initial.routeType);
    setCampaignId(initial.campaignId);
    setQueueName(initial.queueName);
    setEndpoint(initial.endpoint);
    setWho(initial.who);
    setRepId(initial.repId);
    setSoundFile(initial.soundFile);
    setDescription(initial.description);
    setEnabled(initial.enabled);
    setTagDraft(null);
  }, [open, did]);

  if (!did) return null;

  const campaign = campaigns?.find((c) => c.id === campaignId);
  // The reps a campaign number may name: those on the campaign's primary line, where it parks the
  // caller. The rep the number names already stays on offer, marked, if they have left it.
  const reps = [
    ...new Map(
      (members.data ?? [])
        .filter((m) => m.queue_id === campaign?.queue_id && m.sip_endpoint_id !== null)
        .map((m) => [m.sip_endpoint_id as number, m.display_name ?? m.user]),
    ),
  ];
  const namedRep = repId === null ? null : (reps.find(([id]) => id === repId)?.[1] ?? null);
  const savedRepGone =
    did.action_type === "campaign" &&
    did.sip_endpoint_id !== null &&
    did.campaign_id === campaignId &&
    members.data !== undefined &&
    !reps.some(([id]) => id === did.sip_endpoint_id);
  const openCampaigns = (campaigns ?? []).filter((c) => c.enabled || c.id === did.campaign_id);
  const openQueues = (queues ?? []).filter((q) => q.enabled || q.name === did.action_target);
  // The line a tagged caller waits on: the campaign's inbound line, or the queue named.
  const lineName = routeType === "campaign" ? campaign?.queue_name : routeType === "queue" ? queueName : null;
  const line = lineName ? queues?.find((q) => q.name === lineName) : undefined;

  const assignable = catalog.data ? assignableSkills(catalog.data, did.tenant_id) : [];
  const storedTags = catalog.data ? initialTags(did.routing_tags, catalog.data, did.tenant_id) : {};
  const tags = tagDraft ?? storedTags;

  const initial = routeForm(did);
  // The endpoint by id: one picked from the list can name its rep differently from the copy on
  // the number and still be the same phone.
  const changed = !sameValues(
    {
      routeType, campaignId, queueName, endpoint: endpoint?.id, who, repId, soundFile, description,
      enabled, tags,
    },
    { ...initial, endpoint: initial.endpoint?.id, tags: storedTags },
  );

  const problem = (() => {
    if (routeType === "campaign") {
      if (!campaignId) return "Choose a campaign.";
      if (campaign && !campaign.queue_name) return `${campaign.name} has no inbound queue yet — set one on the campaign first.`;
      if (who === "rep" && repId === null) return "Choose the rep this number rings first.";
    }
    if (routeType === "queue" && !queueName) return "Choose a queue.";
    if (needsEndpoint && endpoint === null) {
      return routeType === "room" ? "Choose whose room." : "Choose a rep's endpoint.";
    }
    return null;
  })();

  const submit = async () => {
    setFormError(null);
    try {
      await update.mutateAsync({
        id: did.id,
        action_type: routeType,
        action_target:
          routeType === "queue" ? queueName : routeType === "hangup" ? soundFile.trim() || null : null,
        campaign_id: campaignId,
        sip_endpoint_id: needsEndpoint
          ? (endpoint?.id ?? null)
          : routeType === "campaign" && who === "rep"
            ? repId
            : null,
        routing_tags: takesTags && catalog.data ? routingTagsPayload(tags, assignable) : undefined,
        description: description.trim() || null,
        enabled,
      });
      toast.success("Route saved", did.number);
      onOpenChange(false);
    } catch (e) {
      setFormError(e instanceof ApiError ? e.message : "Could not save the route.");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent variant="drawer">
        <DiscardGuard changed={changed} />
        <DialogHeader>
          <DialogTitle>Route {did.number}</DialogTitle>
          <DialogDescription>
            What happens when someone calls this number.
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="space-y-4">
          {formError && <Callout tone="danger">{formError}</Callout>}

          <Field label="Description" htmlFor="did-description" hint="What this line is for.">
            <Input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Larry H Parker — CA intake line"
              autoComplete="off"
            />
          </Field>

          <Field label="Route to" htmlFor="did-route-type" required>
            <Select
              value={routeType ?? UNROUTED}
              onValueChange={(v) => setRouteType(v === UNROUTED ? null : (v as RouteType))}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ROUTE_TYPES.map((t) => (
                  <SelectItem key={t} value={t} description={ROUTE_DESCRIPTIONS[t]}>
                    {ROUTE_LABELS[t]}
                  </SelectItem>
                ))}
                <SelectItem
                  value={UNROUTED}
                  description="Callers hear that the number cannot be reached, and the switch logs it."
                >
                  Not routed
                </SelectItem>
              </SelectContent>
            </Select>
          </Field>

          {routeType === "campaign" && (
            <Field label="Campaign" htmlFor="did-campaign" required>
              <Select value={campaignId ?? ""} onValueChange={setCampaignId}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose a campaign" />
                </SelectTrigger>
                <SelectContent>
                  {openCampaigns.map((c) => (
                    <SelectItem
                      key={c.id}
                      value={c.id}
                      description={
                        c.queue_name ? `Inbound queue: ${c.queue_name}` : "No inbound queue yet"
                      }
                    >
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          )}

          {routeType === "campaign" && campaignId && (
            <fieldset className="min-w-0 space-y-2">
              <legend className="mb-2 text-xs font-medium text-text">Who answers</legend>
              <div className="grid gap-2 sm:grid-cols-2">
                <WhoChoice
                  checked={who === "line"}
                  onSelect={() => setWho("line")}
                  title="Anyone on the line"
                  description="Whoever is free, by the line's skills."
                />
                <WhoChoice
                  checked={who === "rep"}
                  onSelect={() => setWho("rep")}
                  title="One rep"
                  description="Their phone rings first."
                />
              </div>
            </fieldset>
          )}

          {routeType === "campaign" && campaignId && who === "rep" && (
            <Field
              label="Rep"
              htmlFor="did-rep"
              required
              hint={`Reps who staff ${campaign?.name ?? "the campaign"}. Add one on its Staffing tab.`}
            >
              <Select
                value={repId === null ? "" : String(repId)}
                onValueChange={(v) => setRepId(Number(v))}
              >
                <SelectTrigger id="did-rep">
                  <SelectValue placeholder={members.isPending ? "Loading the reps…" : "Choose a rep"} />
                </SelectTrigger>
                <SelectContent>
                  {reps.map(([id, name]) => (
                    <SelectItem key={id} value={String(id)}>
                      {name}
                    </SelectItem>
                  ))}
                  {savedRepGone && did.sip_endpoint_id !== null && (
                    <SelectItem value={String(did.sip_endpoint_id)} description="No longer staffs the campaign">
                      {did.sip_endpoint_user_name ?? did.sip_endpoint_extension ?? "The saved rep"}
                    </SelectItem>
                  )}
                </SelectContent>
              </Select>
            </Field>
          )}

          {routeType === "campaign" && campaign && who === "rep" && (
            <Callout tone="info">
              When {namedRep ?? "the rep"} can't take a call: {repNumberRule(campaign)}. That is{" "}
              {campaign.name}'s rule for every rep's number;{" "}
              <Link to={`/campaigns/${campaign.id}`} className="underline underline-offset-2">
                change it on its Routing tab
              </Link>
              .
            </Callout>
          )}

          {routeType === "queue" && (
            <Field label="Queue" htmlFor="did-queue" required>
              <Select value={queueName ?? ""} onValueChange={setQueueName}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose a queue" />
                </SelectTrigger>
                <SelectContent>
                  {openQueues.map((q) => (
                    <SelectItem key={q.id} value={q.name} description={q.description ?? undefined}>
                      {q.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          )}

          {takesTags && (
            <section className="space-y-2" aria-labelledby="did-tags-label">
              <div>
                <p id="did-tags-label" className="text-xs font-medium text-text">
                  Callers need these skills
                </p>
                <p className="text-xs text-text-muted">
                  {routeType === "campaign" && who === "rep" ? (
                    <>
                      Used when the call goes to the line: ringing {namedRep ?? "the rep"} first
                      ignores skills.
                    </>
                  ) : (
                    <>
                      Calls to this number ring only reps who hold every skill ticked, at the level
                      asked or higher. Tick Spanish to make this the Spanish line.
                    </>
                  )}
                </p>
              </div>

              {catalog.isPending ? (
                <Skeleton className="h-24" />
              ) : !catalog.data ? (
                <Callout tone="danger">
                  Could not load the skills. Saving keeps this number's skills as they are.
                </Callout>
              ) : did.tenant_id === null ? (
                <Callout tone="info">
                  This number belongs to no tenant, so there is no skill catalog to pick from.
                </Callout>
              ) : assignable.length === 0 ? (
                <p className="rounded-md border border-dashed border-border px-3 py-4 text-center text-xs text-text-muted">
                  The catalog has no rep skills yet.{" "}
                  <Link to="/skills" className="text-accent-11 hover:underline">
                    Add one on Skills.
                  </Link>
                </p>
              ) : (
                <ul className="divide-y divide-border rounded-md border border-border">
                  {assignable.map((skill) => (
                    <TagChoice
                      key={skill.id}
                      skill={skill}
                      level={tags[skill.slug]}
                      onChange={(level) => {
                        const next = { ...tags };
                        if (level === null) delete next[skill.slug];
                        else next[skill.slug] = level;
                        setTagDraft(next);
                      }}
                    />
                  ))}
                </ul>
              )}

              {catalog.data && (
                <TagNotes
                  did={did}
                  catalog={catalog.data}
                  assignable={assignable}
                  tags={tags}
                  wait={line && Object.keys(tags).length > 0 ? taggedCallerWait(line) : null}
                />
              )}
            </section>
          )}

          {needsEndpoint && (
            <Field
              label="Whose phone"
              htmlFor="did-endpoint"
              required
              hint={
                routeType === "room" ? (
                  <>
                    Callers ring this rep's phone and, once answered, the call lives in a room of
                    its own: the rep can hold the caller, bring colleagues in and hand the call
                    over. Nobody answering within 30 seconds ends the call.
                  </>
                ) : (
                  "Only endpoints assigned to a user are listed."
                )
              }
            >
              <EndpointPicker
                value={endpoint}
                onChange={setEndpoint}
                mode={{ kind: "assigned" }}
              />
            </Field>
          )}

          {routeType === "hangup" && (
            <Field
              label="Announcement"
              htmlFor="did-sound"
              hint="A sound file on the switch, e.g. ivr/ivr-closed.wav. Leave blank to hang up silently."
            >
              <Input
                className="font-mono"
                value={soundFile}
                onChange={(e) => setSoundFile(e.target.value)}
                placeholder="ivr/ivr-closed.wav"
                autoComplete="off"
              />
            </Field>
          )}

          {routeType !== "campaign" && (
            <Field
              label="Campaign (for reporting)"
              htmlFor="did-attribution"
              hint="Tags calls on this number with a campaign without changing where they go."
            >
              <Select
                value={campaignId ?? NONE}
                onValueChange={(v) => setCampaignId(v === NONE ? null : v)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="None" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>None</SelectItem>
                  {openCampaigns.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          )}

          {problem && <Callout tone="warning">{problem}</Callout>}

          <div className="flex items-center justify-between rounded-md border border-border px-3 py-2.5">
            <div>
              <p className="text-xs font-medium text-text">Enabled</p>
              <p className="text-xs text-text-muted">
                A disabled number is not served to the switch; callers get a not-found.
              </p>
            </div>
            <Switch checked={enabled} onCheckedChange={setEnabled} aria-label="Enabled" />
          </div>
        </DialogBody>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant="default"
            loading={update.isPending}
            disabled={Boolean(problem)}
            onClick={() => void submit()}
          >
            Save route
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** One skill a caller can be required to need: ticked with a minimum level, or not asked for. */
function TagChoice({
  skill, level, onChange,
}: {
  skill: RoutingSkill;
  /** Undefined while not required. */
  level: number | undefined;
  onChange: (level: number | null) => void;
}) {
  const id = `did-tag-${skill.id}`;
  const required = level !== undefined;
  return (
    <li className="flex items-center gap-3 px-3 py-2.5">
      <input
        id={id}
        type="checkbox"
        className="size-3.5 shrink-0 accent-accent-9"
        checked={required}
        onChange={(e) => onChange(e.target.checked ? DEFAULT_TAG_LEVEL : null)}
      />
      <label htmlFor={id} className="min-w-0 flex-1 cursor-pointer">
        <span className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
          <span className="text-sm font-medium text-text">{skill.name}</span>
          <span className="font-mono text-xs text-text-muted">{skill.slug}</span>
          {skill.category && (
            <Badge tone="outline" size="sm">
              {skill.category}
            </Badge>
          )}
        </span>
        <span className="block text-xs text-text-muted">
          {skill.holders === 1 ? "1 rep holds it" : `${skill.holders} reps hold it`}
        </span>
      </label>
      {required ? (
        <Select value={String(level)} onValueChange={(v) => onChange(Number(v))}>
          <SelectTrigger className="w-28 shrink-0" aria-label={`${skill.name} minimum level`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent align="end">
            {LEVELS.map((n) => (
              <SelectItem key={n} value={String(n)}>
                {n === 1 ? "Any level" : `Level ${n}+`}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      ) : (
        <span className="w-28 shrink-0 text-right text-xs text-text-subtle">Not needed</span>
      )}
    </li>
  );
}

/** What the ticked tags mean for a caller: how long they wait, a skill nobody holds, and stored
 *  tags the server no longer takes, which saving removes. */
function TagNotes({
  did, catalog, assignable, tags, wait,
}: {
  did: Did;
  catalog: RoutingSkill[];
  assignable: RoutingSkill[];
  tags: Record<string, number>;
  wait: string | null;
}) {
  const unheld = unheldTags(tags, assignable);
  const dropped = numberTags(did.routing_tags, catalog, did.tenant_id).filter((t) => t.problem);
  return (
    <>
      {wait && <p className="text-xs text-text-muted">{wait}</p>}
      {unheld.length > 0 && (
        <Callout tone="warning">
          No rep holds {unheld.map((s) => s.name).join(" or ")} yet, so calls to this number will
          wait for a rep who never comes. Give it to reps on the Users page.
        </Callout>
      )}
      {dropped.length > 0 && (
        <Callout tone="warning">
          <p>
            {dropped.length === 1
              ? "This number needs a skill the ACD no longer counts. Saving removes it."
              : `This number needs ${dropped.length} skills the ACD no longer counts. Saving removes them.`}
          </p>
          <ul className="mt-1 list-disc space-y-0.5 pl-4">
            {dropped.map((t) => (
              <li key={t.slug}>
                {t.skill?.name ?? t.slug} <span className="font-mono">({t.slug})</span>:{" "}
                {t.problem?.toLowerCase()}
              </li>
            ))}
          </ul>
        </Callout>
      )}
    </>
  );
}

/** One answer to "Who answers": a radio card, as the campaign's own choices are drawn. */
function WhoChoice({
  checked, onSelect, title, description,
}: {
  checked: boolean;
  onSelect: () => void;
  title: string;
  description: string;
}) {
  return (
    <label className="flex cursor-pointer items-start gap-2.5 rounded-md border border-border px-3 py-2.5 has-[:checked]:border-accent-8 has-[:checked]:bg-accent-2">
      <input
        type="radio"
        name="did-who-answers"
        className="mt-0.5 accent-accent-9"
        checked={checked}
        onChange={onSelect}
      />
      <span>
        <span className="block text-xs font-medium text-text">{title}</span>
        <span className="block text-xs text-text-muted">{description}</span>
      </span>
    </label>
  );
}
